import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setModal, selectModal } from '../../../redux/modalSlice'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { Controller, useForm } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { selectPortfolios } from '../../../redux/portfolioSlice'
import { selectUser } from '../../../redux/userSlice'
import Loading from '../../../components/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'

export default function PortfolioDetailsModal() {
  const dispatch = useDispatch()
  const modal = useSelector(selectModal)

  const [saving, setSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const request = useRequest()
  const portfolioId = modal.options?.portfolioId
  const [portfolioInfo, setPortfolioInfo] = useState(null)

  const portfolios = useSelector(selectPortfolios)
  const active = useSelector(selectUser)

  const customerId = modal.options?.customerId
  const navi = useNavigate()

  const { register, setValue, handleSubmit, control, formState: { errors }, reset } = useForm();

  const isActive = modal.is_active && modal.type === 'portfolio_details_modal'
  const inFlightPortfolioId = useRef(null)

  const resetState = () => {
    setSaving(false)
    setPortfolioInfo(null)
    setIsLoading(false)
    reset()
  }

  const getPortfolio = async (portfolioId) => {
    inFlightPortfolioId.current = portfolioId
    setIsLoading(true)
    setPortfolioInfo(null)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/portfolio/${portfolioId}`)
    try {
      const response = await request.get()
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightPortfolioId.current !== portfolioId) return
      if (response.status === 200 || response.status === 201) {
        setPortfolioInfo(response?.data?.data)

        setIsLoading(false)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setIsLoading(false)
      }
    } catch (error) {
      if (inFlightPortfolioId.current !== portfolioId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (portfolioInfo !== null) {
      setValue('has_real_time_payment', !!portfolioInfo.has_real_time_payment)
      setValue('is_sms_mfa_enabled', !!portfolioInfo.is_sms_mfa_enabled)
      setValue('is_cashout_refinance_available', !!portfolioInfo.is_cashout_refinance_available)
      setValue('is_bank_verification_ssv', !!portfolioInfo.is_bank_verification_ssv)
      setValue('is_bank_change_email_verification', !!portfolioInfo.is_bank_change_email_verification)
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!portfolioInfo.collections_refi_after_days) {
        setValue('collections_refi_after_days', !!portfolioInfo.collections_refi_after_days)
      } else {
        setValue('collections_refi_after_days', false)
      }
      setValue('portfolio_id', { label: portfolioInfo?.name, value: portfolioInfo?.id })
    }
  }, [portfolioInfo, setValue])

  useEffect(() => {
    return () => {
      handleCancel()
    };
  }, []);

  // This modal stays mounted in the Footer; clear the previous portfolio's values before loading
  // so a failed load can't leave them in the form behind an enabled Save.
  useEffect(() => {
    if (isActive && portfolioId) {
      resetState()
      getPortfolio(portfolioId)
    }
  }, [isActive, portfolioId])

  const handleCancel = () => {
    inFlightPortfolioId.current = null
    resetState()
    dispatch(setModal({ is_active: false }))
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    request.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/portfolio/${portfolioInfo?.id}/update`
    )
    try {
      const response = await request.modify({
        has_real_time_payment: formData.has_real_time_payment,
        is_sms_mfa_enabled: formData.is_sms_mfa_enabled,
        is_cashout_refinance_available: formData.is_cashout_refinance_available,
        collections_refi_after_days: formData.collections_refi_after_days,
        is_bank_verification_ssv: formData.is_bank_verification_ssv,
        is_bank_change_email_verification: formData.is_bank_change_email_verification,
      })
      if (response.status === 200) {
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: '' }))
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } });
      } else {
        setSaving(false)
        dispatch(
          setNotification({ type: 'error', body: `Error: ${response.status}` })
        )
      }
    } catch (error) {
      setSaving(false)
      const errorMessage =
        error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const parseLabel = ((field) => {
    let option = field.name;
    if (field.label) {
      option = field.label;
    }
    return option
  })

  return isActive ? (
    <form onSubmit={handleSubmit(onSubmit)} id="portfolio_details_modal_form" className="lox-form">
      <div className="modal is-active" data-testid="portfolio-details-modal">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-confirmation column is-9">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              {'Portfolio Details'}
            </p>
            <button
              type="button"
              className="delete"
              aria-label="close"
              data-testid="portfolio-details-modal-close"
              onClick={handleCancel}
            ></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: '30px', overflow: 'hidden' }}
          >
            <div className="columns mb-0 is-flex-direction-column">
              <div className="column">
                {(!isLoading && !!portfolioInfo?.id) ? (
                  <>
                    <h3>Portfolio</h3>
                    <div className="mt-1" data-testid="portfolio-details-modal-portfolio-select">
                      <Controller
                        name="portfolio_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            {...register('portfolio_id')}
                            inputId="portfolio_id"
                            options={
                              portfolios.data.filter((o) => o.id != null && o.is_active == 1).map((o) => ({
                                label: o.name,
                                value: o.id,
                              }))
                            }
                            isSearchable={false}
                            classNames={reactSelectClassNames()}
                            getOptionLabel={(e) => parseLabel(e)}
                            getOptionValue={(e) => parseLabel(e)}
                            onChange={(val) => {
                              field.onChange({ label: val?.label, value: val?.value })
                              // eslint-disable-next-line no-debugger
                              getPortfolio(val?.value)
                            }}
                            menuPosition="fixed"
                            form="portfolio_details_modal_form"
                          />
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <Loading size="is-size-5" />
                )}
              </div>
            </div>

            <div className="columns mb-6 pb-6 is-multiline">
              <div className="column is-one-quarter">
                {portfolioInfo?.id ? (
                  <label htmlFor="has_real_time_payment">
                    <span>Has Realtime Payment <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={`If this box is unchecked, Real Time Originations will disabled for the selected portfolio and Real Time will no longer show as a funding option. Any real time payments that are currently scheduled will be held until it is turned back on. Any in queue will fail.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span></span>
                    <br />
                    <Controller
                      name="has_real_time_payment"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="has_real_time_payment"
                          data-testid="portfolio-details-modal-has-real-time-payment"
                          type="checkbox"
                          checked={field.value}
                          className={'checkbox active-blue'}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      )}
                    />
                  </label>
                ) : null}
              </div>

              <div className="column is-one-quarter">
                {portfolioInfo?.id ? (
                  <label htmlFor="is_sms_mfa_enabled">
                    <span>Has Sms Mfa <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={`If this box is unchecked, Customer Sms Mfa will disabled for the selected portfolio`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span></span>
                    <br />
                    <Controller
                      name="is_sms_mfa_enabled"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="is_sms_mfa_enabled"
                          data-testid="portfolio-details-modal-sms-mfa-enabled"
                          type="checkbox"
                          checked={field.value}
                          className={'checkbox active-blue'}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      )}
                    />
                  </label>
                ) : null}
              </div>

              <div className="column is-one-quarter">
                {portfolioInfo?.id ? (
                  <label htmlFor="is_cashout_refinance_available">
                    <span>Is Cashout Refinance Available <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={`If this box is unchecked, Cash Out Refi will be disabled for the selected portfolio and prevent any in progress from being finalized.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span></span>
                    <br />
                    <Controller
                      name="is_cashout_refinance_available"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="is_cashout_refinance_available"
                          data-testid="portfolio-details-modal-cashout-refinance-available"
                          type="checkbox"
                          checked={field.value}
                          className={'checkbox active-blue'}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      )}
                    />
                  </label>
                ) : null}
              </div>

              <div className="column is-one-quarter">
                {portfolioInfo?.id ? (
                  <label htmlFor="is_bank_verification_ssv">
                    <span>Is DecisionLogic in SSV Active <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={`If this box is unchecked, DecisionLogic will be disabled in SSV for this portfolio. Customers who would otherwise be selected for DecisionLogic will follow standard verification behavior.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span></span>
                    <br />
                    <Controller
                      name="is_bank_verification_ssv"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="is_bank_verification_ssv"
                          data-testid="portfolio-details-modal-bank-verification-ssv"
                          type="checkbox"
                          checked={field.value}
                          className={'checkbox active-blue'}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      )}
                    />
                  </label>
                ) : null}
              </div>

              <div className="column is-one-quarter">
                {portfolioInfo?.id ? (
                  <label htmlFor="is_bank_change_email_verification">
                    <span>Bank Change Email Verification <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={`If this box is unchecked, the SSV Email-Only MFA Challenge before Banking Info will be disabled for this portfolio and customers will proceed straight from Contact Info to Banking Info.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span></span>
                    <br />
                    <Controller
                      name="is_bank_change_email_verification"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="is_bank_change_email_verification"
                          data-testid="portfolio-details-modal-bank-change-email-verification"
                          type="checkbox"
                          checked={field.value}
                          className={'checkbox active-blue'}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      )}
                    />
                  </label>
                ) : null}
              </div>

              <div className="column is-one-quarter">
                {portfolioInfo?.id ? (
                  <label htmlFor="collections_refi_after_days">
                    <span>Collections Refi After Days <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={`If this box is unchecked, Collections Refi will be disabled for the selected portfolio and prevent any in progress from being finalized.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span></span>
                    <br />
                    <Controller
                      name="collections_refi_after_days"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="collections_refi_after_days"
                          data-testid="portfolio-details-modal-collections-refi-after-days"
                          type="checkbox"
                          checked={field.value}
                          className={'checkbox active-blue'}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      )}
                    />
                  </label>
                ) : null}
              </div>
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1 pr-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white mx-1"
                data-testid="portfolio-details-modal-cancel"
                onClick={handleCancel}
              >Cancel
              </button>

              <button
                type="submit"
                className="button is-small is-link"
                data-testid="portfolio-details-modal-save"
                disabled={saving || !portfolioInfo?.id}
              >Save
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>

  ) : null
}
